import {
    ReadonlyMat4,
    ReadonlyVec2,
    ReadonlyVec3,
    mat4,
    vec2,
    vec3,
} from 'gl-matrix';
import { PanZoom } from '../../view/PanZoom';
import { View } from '../../view/View';
import { PlaneView } from '../../view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../EventHandlerResult';
import { ModifierKeys } from '../ModifierKeys';
import { ToolAction } from '../ToolAction';

/**
 * State to store the pan action.
 *
 * {@link PanAction} starts with {@link PanAction.onMousePress} and ends with {@link PanAction.onMouseRelease}.\
 * In between the start and end events the {@link PanActionEventState} is used to store the {@link PanAction.activeEventState state} of the {@link PanAction}.
 */
class PanActionEventState {
    /**
     * The {@link ReadonlyVec3 PatientSpace coordinates} of the mouse when the {@link PanAction} started.
     */
    public readonly startPatient: ReadonlyVec3;

    /**
     * {@link ReadonlyMat4 transformationMatrix} that the {@link View} had when the {@link PanAction} started.\
     * This is a copy of the original {@link ReadonlyMat4 matrix} as the original is manipulated during the {@link PanAction}.
     */
    public readonly transformationMatrix: ReadonlyMat4;

    /**
     * The {@link PanZoom.translation translation} {@link ReadonlyVec3 coordinates} when the {@link PanAction} started.
     */
    public readonly panCenter: ReadonlyVec3;

    public constructor(
        public readonly view: PlaneView,
        startPatient: ReadonlyVec3,
        transformationMatrix: ReadonlyMat4,
        panCenter: ReadonlyVec3,
        public readonly startMouseCoordinates: ReadonlyVec2 // where the mouse event started
    ) {
        this.startPatient = startPatient;
        this.transformationMatrix = transformationMatrix;
        this.panCenter = panCenter;
    }

    /**
     * Method to get the updated {@link PanZoom._translation translation} of the {@link PanAction}.
     * @param coordinates The mouse position in client pixel coordinates at the time of the event.\
     * These are converted into viewRelativeCoordinates in between (-1, 1) and then further into patientSpaceCoordinates.
     * @returns The new {@link ReadonlyVec3 PatientSpaceCoordinates} that are to be assigned to the {@link PanZoom._translation PanZoom.translation}.
     */
    public getUpdatedCenter(coordinates: ReadonlyVec2): ReadonlyVec3 {
        const viewCoordinates = this.view.clientToViewCoordinates(coordinates);
        const patientCoordinates = vec3.transformMat4(
            vec3.create(),
            vec3.fromValues(viewCoordinates[0], viewCoordinates[1], 0),
            this.transformationMatrix
        );
        const delta = vec3.subtract(
            vec3.create(),
            patientCoordinates,
            this.startPatient
        ); //delta patient
        return vec3.subtract(vec3.create(), this.panCenter, delta);
    }
}

/**
 * {@link PanAction} provides functionality to modify the {@link PanZoom._translation translation} of a {@link PanZoom}-object.
 */
export class PanAction extends ToolAction {
    /**
     * The {@link PanZoom}-object that this {@link PanAction} will modify.
     */
    private readonly panZoom: PanZoom;

    /**
     * Mouse button to which this {@link PanAction} should react to.
     */
    private readonly mouseButton: number;

    /**
     * {@link ModifierKeys} that need to be active to use this {@link PanAction}.
     */
    private readonly modifierKeys: ModifierKeys;

    /**
     * {@link PanAction} constructor, which takes the {@link PanZoom}-object, {@link mouseButton} and optional {@link ModifierKeys} as parameters.
     * @param panZoom The {@link PanZoom}-object that is to be altered by this {@link PanAction}.
     * @param mouseButton The mouseButton this {@link PanAction} uses.
     * @param modifierKeys {@link ModifierKeys} that need to be active to use this {@link PanAction}. Defaults to {@link ModifierKeys.NONE NONE}.
     */
    public constructor(
        panZoom: PanZoom,
        mouseButton: number,
        modifierKeys: ModifierKeys = ModifierKeys.NONE
    ) {
        super();
        this.panZoom = panZoom;
        this.mouseButton = mouseButton;
        this.modifierKeys = modifierKeys;
    }

    private _startState: PanActionEventState | undefined;

    /**
     * MousePress event that was caught by the canvas and propagated by {@link ToolManager} to this {@link PanAction}.\
     * This sets the {@link PanAction.activeEventState activeEventState} and starts a {@link PanAction} if the event is successfully handled.
     * @param button The button that triggered the event
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.START_ACTION} if
     * 1. the {@link button} equals {@link PanAction.mouseButton},
     * 2. the {@link modifierKeys} equal {@link PanAction.modifierKeys} and
     * 3. the {@link view} is an instanceof {@link PlaneView},
     *
     * in which case the event was successfully handled and {@link PanAction} was started.
     *
     * If any of the conditions is not met, the event is not handled and {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} is returned.
     */
    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: View
    ): EventHandlerResult {
        if (
            button === this.mouseButton &&
            modifierKeys.equals(this.modifierKeys) &&
            view instanceof PlaneView
        ) {
            if (!this._startState) {
                const transformationMatrix = mat4.clone(
                    view.transformationMatrix
                );
                const viewCoordinates =
                    view.clientToViewCoordinates(coordinates);
                this._startState = new PanActionEventState(
                    view,
                    vec3.transformMat4(
                        vec3.create(),
                        vec3.fromValues(
                            viewCoordinates[0],
                            viewCoordinates[1],
                            0
                        ),
                        transformationMatrix
                    ),
                    transformationMatrix,
                    vec3.clone(view.position),
                    coordinates
                );
                return new EventHandlerResult(
                    EventHandlerResultType.EVENT_HANDLED,
                    true
                );
            }
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * MouseMove event that was caught by the canvas and propagated by {@link ToolManager} to this {@link PanAction}.\
     * This updates the {@link panZoom}-object's {@link PanZoom._translation translation} coordinates if the event is successfully handled.
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_HANDLED EVENT_HANDLED} if
     * the {@link activeEventState} is defined, in which case the event was successfully handled.\
     * {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} if the event was not handled.
     */
    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        _view: View
    ): EventHandlerResult {
        if (this._startState && _view instanceof PlaneView) {
            _view.panTo(this._startState.getUpdatedCenter(coordinates));

            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * MouseRelease event that was caught by the canvas and propagated by {@link ToolManager} to this {@link PanAction}.\
     * If this {@link PanAction} is active, in which case it has a {@link PanAction.activeEventState activeEventState},\
     * the {@link panZoom}-object's {@link PanZoom._translation translation} coordinates are updated, the {@link PanAction.activeEventState activeEventState} is erased,
     * this {@link PanAction} is finished and {@link EventHandlerResult} of type {@link EventHandlerResultType.FINISH_ACTION FINISH_ACTION} is returned.
     *
     * If {@link PanAction.activeEventState activeEventState} is undefined {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} is returned.
     * @param button The button that triggered the event
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.FINISH_ACTION FINISH_ACTION} if {@link PanAction.activeEventState activeEventState} is defined,\
     * {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} otherwise.
     */
    public onMouseRelease(
        _button: number,
        coordinates: ReadonlyVec2
    ): EventHandlerResult {
        let ret = new EventHandlerResult(
            EventHandlerResultType.EVENT_NOT_HANDLED
        );
        if (
            this._startState &&
            !vec2.equals(coordinates, this._startState.startMouseCoordinates)
        ) {
            ret = new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        } else {
            // if the mouse didn't move, we want to try select the ROI under the cursor
            this._startState?.view.selectRoiAtCoords(coordinates);
        }

        this._startState = undefined;
        return ret;
    }
}
